import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getUserRole, getEmpresaId } from '../services/sessionService';
//import { runPDFTest } from "../document-intelligence/tests/pdf-test";

function Instrumentos() {
  const rol = getUserRole();
  const navigate = useNavigate();

  const [instrumentos, setInstrumentos] = useState([]);
  const [nombresCamposExtras, setNombresCamposExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [mensajeError, setMensajeError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [columnaOrden, setColumnaOrden] = useState(() => localStorage.getItem('columnaOrden') || null);
  const [direccionOrden, setDireccionOrden] = useState(() => localStorage.getItem('direccionOrden') || 'asc');

  const ordenarPor = (columna) => {
    if (columna === columnaOrden) {
      const nuevaDireccion = direccionOrden === 'asc' ? 'desc' : 'asc';
      setDireccionOrden(nuevaDireccion);
      localStorage.setItem('direccionOrden', nuevaDireccion);
    } else {
      setColumnaOrden(columna);
      setDireccionOrden('asc');
      localStorage.setItem('columnaOrden', columna);
      localStorage.setItem('direccionOrden', 'asc');
    }
    if (columna !== columnaOrden) localStorage.setItem('columnaOrden', columna);
  };

  const convertirFecha = (fechaStr) => {
    if (!fechaStr) return new Date('1900-01-01');
    const partes = fechaStr.split('/');
    if (partes.length !== 3) return new Date('1900-01-01');
    const [dia, mes, año] = partes.map(Number);
    return new Date(año, mes - 1, dia);
  };

  const instrumentosFiltrados = instrumentos.filter((ins) =>
    Object.values(ins).some((valor) =>
      valor?.toString().toLowerCase().includes(busqueda.toLowerCase())
    )
  );

  const instrumentosOrdenados = [...instrumentosFiltrados].sort((a, b) => {
    if (!columnaOrden) return 0;

    if (columnaOrden === 'fechaUltima' || columnaOrden === 'fechaProxima') {
      const dateA = convertirFecha(a[columnaOrden]);
      const dateB = convertirFecha(b[columnaOrden]);
      return direccionOrden === 'asc' ? dateA - dateB : dateB - dateA;
    }

    const valA = a[columnaOrden]?.toString().toLowerCase() ?? '';
    const valB = b[columnaOrden]?.toString().toLowerCase() ?? '';
    if (valA < valB) return direccionOrden === 'asc' ? -1 : 1;
    if (valA > valB) return direccionOrden === 'asc' ? 1 : -1;
    return 0;
  });


  useEffect(() => {
    let cancelado = false;

    const fetchInstrumentos = async () => {
      setLoading(true);
      setErrorCarga('');

      try {
        const empresaId = getEmpresaId();

        // 1) Instrumentos
        const { data: instrumentosData, error: instrumentosError } = await supabase
          .from('instrumentos')
          .select('*')
          .eq('activo', true)
          .eq('empresa_id', empresaId);

        if (instrumentosError) {
          throw new Error(`Instrumentos: ${instrumentosError.message}`);
        }

        // Adaptador DEV/PROD
        const instrumentosBase = (instrumentosData || []).map((i) => {
          const fechaUlt =
            i.fecha_ultima ??
            i.fechaUltima ??
            i.fechaUltimaCalibracion ??
            null;

          const fechaProx =
            i.fecha_proxima ??
            i.fechaProxima ??
            i.fechaProximaCalibracion ??
            null;

          return {
            ...i,
            rango: i.rango_medicion ?? i.rango ?? i.rangoMedicion ?? '',
            serie: i.numero_serie ?? i.serie ?? i.numeroSerie ?? '',
            fechaUltima: fechaUlt,
            fechaProxima: fechaProx,
            asignado: i.asignado ?? i.asignado_a ?? i.asignadoA ?? '',
          };
        });

        // 2) Campos personalizados
        const { data: camposExtra, error: camposError } = await supabase
          .from('campos_personalizados')
          .select('*')
          .eq('empresa_id', empresaId);

        if (camposError) {
          throw new Error(`Campos personalizados: ${camposError.message}`);
        }

        // 3) Nombres únicos
        const nombresUnicos = Array.from(
          new Set((camposExtra || []).map((c) => c.nombre_campo))
        );

        // 4) Merge instrumentos + extras
        const instrumentosConExtras = instrumentosBase.map((instr) => {
          const extras = (camposExtra || [])
            .filter((c) => c.instrumento_id === instr.id)
            .reduce((acc, curr) => {
              acc[curr.nombre_campo] = curr.valor_campo;
              return acc;
            }, {});

          return { ...instr, ...extras };
        });

        if (!cancelado) {
          setNombresCamposExtras(nombresUnicos);
          setInstrumentos(instrumentosConExtras);
        }
      } catch (error) {
        console.error('❌ Error cargando instrumentos:', error);

        if (!cancelado) {
          setInstrumentos([]);
          setNombresCamposExtras([]);
          setErrorCarga(
            'No se pudieron cargar los instrumentos. Verifica tu conexión e inténtalo nuevamente.'
          );
       }
      } finally {
        if (!cancelado) {
          setLoading(false);
        }
      }
    };

    fetchInstrumentos();

    return () => {
      cancelado = true;
    };
  }, []);

  const reiniciarOrden = () => {
    setColumnaOrden(null);
    setDireccionOrden('asc');
    localStorage.removeItem('columnaOrden');
    localStorage.removeItem('direccionOrden');
  };

  const handleEliminar = async (id) => {
    const confirmar = window.confirm(
      '¿Estás seguro que deseas eliminar este instrumento? Esta acción no se puede deshacer.'
    );

    if (!confirmar) return;

    setMensaje('');
    setMensajeError('');

    const { error } = await supabase
      .from('instrumentos')
      .update({ activo: false })
      .eq('id', id);

    if (error) {
      console.error('❌ Error al eliminar instrumento:', error);
      setMensajeError(
        'No se pudo eliminar el instrumento. Verifica tu conexión e inténtalo nuevamente.'
      );
      return;
    }

    // Actualiza lista local sin re-fetch completo
    setInstrumentos((prev) => prev.filter((i) => i.id !== id));
    setMensaje('Instrumento eliminado correctamente.');
  };

  const exportarAExcel = () => {
    const datos = instrumentosOrdenados.map((ins) => ({
      Nombre: ins.nombre,
      Identificación: ins.identificacion,
      'Rango de Medición': ins.rango,
      Marca: ins.marca,
      Modelo: ins.modelo,
      'Número de Serie': ins.serie,
      Tolerancia: ins.tolerancia,
      Tipo: ins.tipo,
      Condición: ins.condicion,
      Certificado: ins.certificado,
      'Fecha Última': ins.fechaUltima,
      'Fecha Próxima': ins.fechaProxima,
      Carta: ins.carta,
      Ubicación: ins.ubicacion,
      'Asignado a': ins.asignado,
      Proveedor: ins.proveedor,
      Precio: ins.precio,
      Notas: ins.notas,
    }));

    const hoja = XLSX.utils.json_to_sheet(datos);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Instrumentos');
    const excelBuffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Instrumentos.xlsx');
  };

  // Convierte valor de fecha de Excel (número, Date o string) a 'YYYY-MM-DD'
  const convertirFechaExcel = (valor) => {
    if (!valor) return null;

    // Si ya es Date
    if (valor instanceof Date) {
      const y = valor.getFullYear();
      const m = String(valor.getMonth() + 1).padStart(2, '0');
      const d = String(valor.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    // Si es número (fecha serial de Excel, tipo 46053)
    if (typeof valor === 'number') {
      // Usamos la fórmula estándar: días desde 1970 = valor - 25569
      const fechaJs = new Date(Math.round((valor - 25569) * 86400 * 1000));
      const y = fechaJs.getFullYear();
      const m = String(fechaJs.getMonth() + 1).padStart(2, '0');
      const d = String(fechaJs.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    // Si es string
    if (typeof valor === 'string') {
      const texto = valor.trim();

      // Si ya viene como 'YYYY-MM-DD', lo dejamos igual
      if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return texto;

      // Si viene como 'DD/MM/AAAA' o 'DD-MM-AAAA'
      const partes = texto.split(/[-/]/);
      if (partes.length === 3) {
        let [d, m, y] = partes;
        d = d.padStart(2, '0');
        m = m.padStart(2, '0');
        if (y.length === 2) y = '20' + y; // por si acaso
        return `${y}-${m}-${d}`;
      }
    }

    // Si no se pudo interpretar, devolvemos null
    return null;
  };


  const importarDesdeExcel = async (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;

    setMensaje('');
    setMensajeError('');

    const lector = new FileReader();

    lector.onload = async (evento) => {
      const datosBinarios = evento.target.result;
      const libro = XLSX.read(datosBinarios, { type: 'binary' });
      const hoja = libro.Sheets[libro.SheetNames[0]];
      const datos = XLSX.utils.sheet_to_json(hoja);

      if (datos.length === 0) {
        setMensajeError('El archivo seleccionado no contiene datos.');
        return;
      }

      // 1️⃣ Normalizar encabezados y construir mapeo
      const normalizarClave = (clave) =>
        clave
          .toLowerCase()
          .normalize('NFD')                 // separa acentos
          .replace(/[\u0300-\u036f]/g, '')  // quita acentos
          .replace(/\./g, '')               // quita puntos (Últim., Próxi.)
          .replace(/\s+/g, '')              // quita espacios
          .trim();

      const mapeo = {};
      const ejemplo = datos[0];
      for (let key in ejemplo) {
        mapeo[normalizarClave(key)] = key;
      }

      // 2️⃣ Helpers para obtener campos clave
      function obtenerIdentificacion(row) {
        const keyId = mapeo['identificacion'];
        if (!keyId) return '';
        const raw = row[keyId];
        if (raw === undefined || raw === null) return '';
        return String(raw).trim();
      }

      function obtenerNombre(row) {
        const keyNombre = mapeo['nombre'];
        if (!keyNombre) return '';
        const raw = row[keyNombre];
        if (raw === undefined || raw === null) return '';
        return String(raw).trim();
      }

      const empresaId = getEmpresaId();

      // 3️⃣ IDs del Excel usando el helper
      const idsExcel = datos
        .map((row) => obtenerIdentificacion(row))
        .filter((id) => id !== '');

      if (idsExcel.length === 0) {
        setMensajeError(
          'El archivo no contiene identificaciones válidas para importar.'
        );
        return;
      }

      let exitosos = 0;
      let incompletos = 0;

      // 4️⃣ Buscar en Supabase cuáles identificaciones ya existen para ESA empresa
      const { data: existentes, error: errorVerificacion } = await supabase
        .from('instrumentos')
        .select('identificacion')
        .eq('empresa_id', empresaId)
        .in('identificacion', idsExcel);

      if (errorVerificacion) {
        console.error('❌ Error al verificar duplicados:', errorVerificacion);
        setMensajeError(
          'No se pudo verificar si existen instrumentos duplicados. Inténtalo nuevamente.'
        );
        return;
      }

      const idsExistentes = new Set(existentes.map((d) => d.identificacion));
      const idsVistos = new Set(idsExistentes); // BD + lo que vayamos leyendo
      const duplicados = [];
      const filasParaInsertar = [];

      // 5️⃣ Recorrer todas las filas y clasificar
      for (const row of datos) {
        const identificacion = obtenerIdentificacion(row);
        const nombre = obtenerNombre(row);

        // Fila incompleta (sin nombre o identificación)
        if (!identificacion || !nombre) {
          incompletos++;
          continue;
        }

        // Duplicado respecto a BD o dentro del mismo archivo
        if (idsVistos.has(identificacion)) {
          duplicados.push(row);
          continue;
        }

        // Primera vez que vemos esta identificación → la marcamos como vista
        idsVistos.add(identificacion);

        // Construimos el objeto listo para insertar
        const instrumento = {
          nombre,
          identificacion,
          rango_medicion: row[mapeo['rango']],
          marca: row[mapeo['marca']],
          modelo: row[mapeo['modelo']],
          numero_serie: row[mapeo['serie']],
          tolerancia: row[mapeo['tolerancia']],
          tipo: row[mapeo['tipo']],
          condicion: row[mapeo['condicion']],
          certificado: row[mapeo['certificado']],
          fecha_ultima: convertirFechaExcel(row[mapeo['fechaultima']]),
          fecha_proxima: convertirFechaExcel(row[mapeo['fechaproxima']]),
          carta: row[mapeo['carta']],
          ubicacion: row[mapeo['ubicacion']],
          asignado: row[mapeo['asignado']],
          proveedor: row[mapeo['proveedor']],
          precio: row[mapeo['precio']],
          notas: row[mapeo['notas']],
          activo: true,
          empresa_id: empresaId,
        };

        filasParaInsertar.push(instrumento);
      }

      // 6️⃣ Insertar solo las filas no duplicadas ni incompletas
      if (filasParaInsertar.length > 0) {
        const { data: insertados, error: errorInsert } = await supabase
          .from('instrumentos')
          .insert(filasParaInsertar)
          .select();

        if (errorInsert) {
          console.error('❌ Error durante la inserción:', errorInsert);
          setMensajeError(
            'No se pudieron guardar los instrumentos importados. Inténtalo nuevamente.'
          );
          return;
        }

        // Supabase puede devolver "data" o no, por eso el fallback
        exitosos = insertados?.length || filasParaInsertar.length;
        const nuevosInstrumentos = (insertados || []).map((i) => ({
          ...i,
          rango: i.rango_medicion ?? '',
          serie: i.numero_serie ?? '',
          fechaUltima: i.fecha_ultima ?? null,
          fechaProxima: i.fecha_proxima ?? null,
          asignado: i.asignado ?? '',
        }));

        setInstrumentos((prev) => [...prev, ...nuevosInstrumentos]);
      }

      setMensaje(
        `Importación completada. Guardados: ${exitosos} · ` +
        `Duplicados ignorados: ${duplicados.length} · ` +
        `Incompletos ignorados: ${incompletos}`
      );
    };

    lector.readAsBinaryString(archivo);
  };

  const ColumnHeader = ({ label, col }) => (
    <th
      className="cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100"
      onClick={() => ordenarPor(col)}
      title={`Ordenar por ${label}`}
    >
      {label}{' '}
      <span className="text-cyan-600">
        {columnaOrden === col
          ? direccionOrden === 'asc'
            ? '▲'
            : '▼'
          : ''}
      </span>
    </th>
  );

  return (   
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
    {/*
    <div style={{ margin: "16px 0" }}>
      <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
        Prueba SmartGage Document Intelligence
      </label>

      <input
        type="file"
        accept="application/pdf"
        onChange={async (e) => {
          const file = e.target.files[0];
          if (file) {
            // await runPDFTest(file);
          }
        }}
      />
    </div>
    */}

    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-600">
          Instrumentos
        </p>

        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Matriz de Instrumentos
        </h1>

        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Consulta, administra y mantén actualizada la información de tus
          instrumentos de medición.
        </p>
      </div>

      <button
        onClick={() => navigate('/instrumentos/nuevo')}
        className="inline-flex h-10 items-center justify-center rounded-lg bg-cyan-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700"
      >
        Agregar instrumento
      </button>
    </div>

    {mensajeError && (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {mensajeError}
      </div>
    )}

    {mensaje && (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
        {mensaje}
      </div>
    )}

    {/* Controles superiores */}
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div>
          <label
            htmlFor="busqueda-instrumentos"
            className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            Buscar instrumentos
          </label>

          <input
            id="busqueda-instrumentos"
            type="text"
            placeholder="Buscar por nombre, identificación, marca, modelo..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[16px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          />
        </div>

        <div className="flex flex-wrap gap-2 lg:justify-end">
          <button
            onClick={reiniciarOrden}
            className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Restablecer orden
          </button>

          <button
            onClick={exportarAExcel}
            className="h-10 rounded-lg border border-slate-700 bg-slate-800 px-3 text-sm font-medium text-white transition hover:bg-slate-700"
          >
            Exportar a Excel
          </button>

          {rol === 'superadmin' && (
            <>
              <button
                onClick={() => navigate('/empresas')}
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
              >
                Empresas
              </button>

              <button
                onClick={() => navigate('/admin/nueva-empresa')}
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
              >
                Nueva empresa + admin
              </button>
            </>
          )}
        </div>
      </div>

      {rol === 'superadmin' && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Importar desde Excel
          </label>

          <input
            type="file"
            accept=".xlsx"
            onChange={importarDesdeExcel}
            className="block w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600"
          />
        </div>
      )}
    </div>

      {/* Cards en móvil */}
      <div className="grid gap-3 sm:hidden">
        {loading ? (
          <div className="rounded border p-3 text-center text-gray-500">
            Cargando instrumentos...
          </div>
        ) : errorCarga ? (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-center text-red-700">
            {errorCarga}
          </div>
        ) : instrumentosOrdenados.length === 0 ? (
          <div className="rounded border p-3 text-center text-gray-500">
            No hay instrumentos registrados
          </div>
        ) : (
          instrumentosOrdenados.map((ins) => {
            // Estado visual según fecha próxima
            let estadoTexto = 'Sin fecha';
            let estadoClase =
              'border-slate-200 bg-slate-50 text-slate-600';

            if (ins.fechaProxima) {
              const fechaProx = new Date(ins.fechaProxima);
              const hoy = new Date();
              const diff = Math.floor(
                (fechaProx - hoy) / (1000 * 60 * 60 * 24)
              );

              if (diff < 0) {
               estadoTexto = 'Vencido';
               estadoClase =
                  'border-red-200 bg-red-50 text-red-700';
              } else if (diff <= 30) {
                estadoTexto = 'Por vencer';
                estadoClase =
                  'border-amber-200 bg-amber-50 text-amber-700';
              } else {
                estadoTexto = 'Vigente';
                estadoClase =
                  'border-emerald-200 bg-emerald-50 text-emerald-700';
              }
            }

            return (
              <div
                key={ins.id}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">
                      {ins.nombre || 'Sin nombre'}
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                      {ins.identificacion || 'Sin identificación'}
                      {ins.marca && ` · ${ins.marca}`}
                      {ins.modelo && ` ${ins.modelo}`}
                    </div>
                  </div>

                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${estadoClase}`}
                  >
                    {estadoTexto}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-100 pt-3 text-xs">
                  <div>
                    <span className="block text-slate-400">Tipo</span>
                    <span className="text-slate-700">{ins.tipo || '—'}</span>
                  </div>

                  <div>
                    <span className="block text-slate-400">Condición</span>
                    <span className="text-slate-700">{ins.condicion || '—'}</span>
                  </div>

                  <div>
                    <span className="block text-slate-400">Certificado</span>
                    <span className="text-slate-700">{ins.certificado || '—'}</span>
                  </div>

                  <div>
                    <span className="block text-slate-400">Próxima calibración</span>
                    <span className="text-slate-700">{ins.fechaProxima || '—'}</span>
                  </div>

                  <div className="col-span-2">
                    <span className="block text-slate-400">Ubicación</span>
                    <span className="text-slate-700">{ins.ubicacion || '—'}</span>
                  </div>
                </div>

                {nombresCamposExtras.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-xs">
                    {nombresCamposExtras.map((campo) => (
                      <div key={campo}>
                        <span className="block text-slate-400">{campo}</span>
                        <span className="text-slate-700">{ins[campo] || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}

                {ins.activo && (
                  <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3">
                    <button
                      onClick={() => navigate(`/instrumentos/editar/${ins.id}`)}
                      className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
                    >
                      Editar
                    </button>

                    <button
                      onClick={() => handleEliminar(ins.id)}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-500 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Tabla en md+ */}
      <div className="hidden sm:block">
        {/* Contenedor con scroll vertical y horizontal */}
        <div
          className="mt-2 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm"
          style={{ maxHeight: '60vh' }}
        >
          {/* Forzamos ancho mínimo para que aparezca scroll horizontal cuando haga falta */}
          <div className="min-w-[1200px]">
            <table className="w-full table-auto border-collapse text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  <ColumnHeader label="Nombre" col="nombre" />
                  <ColumnHeader label="Identificación" col="identificacion" />
                  <ColumnHeader label="Rango de Medición" col="rango" />

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 md:table-cell"
                    onClick={() => ordenarPor('marca')}
                  >
                    Marca{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'marca'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 md:table-cell"
                    onClick={() => ordenarPor('modelo')}
                  >
                    Modelo{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'modelo'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 lg:table-cell"
                    onClick={() => ordenarPor('serie')}
                  >
                    Número de Serie{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'serie'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <ColumnHeader label="Tolerancia" col="tolerancia" />
                  <ColumnHeader label="Tipo" col="tipo" />

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 md:table-cell"
                    onClick={() => ordenarPor('condicion')}
                  >
                    Condición{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'condicion'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 lg:table-cell"
                    onClick={() => ordenarPor('certificado')}
                  >
                    Certificado{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'certificado'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 lg:table-cell"
                    onClick={() => ordenarPor('fechaUltima')}
                  >
                    Fecha Última{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'fechaUltima'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 lg:table-cell"
                    onClick={() => ordenarPor('fechaProxima')}
                  >
                    Fecha Próxima{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'fechaProxima'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <ColumnHeader label="Carta" col="carta" />

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 md:table-cell"
                    onClick={() => ordenarPor('ubicacion')}
                  >
                    Ubicación{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'ubicacion'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 lg:table-cell"
                    onClick={() => ordenarPor('asignado')}
                  >
                    Asignado a{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'asignado'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 xl:table-cell"
                    onClick={() => ordenarPor('proveedor')}
                  >
                    Proveedor{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'proveedor'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 xl:table-cell"
                    onClick={() => ordenarPor('precio')}
                  >
                    Precio{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'precio'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th
                    className="hidden cursor-pointer whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 xl:table-cell"
                    onClick={() => ordenarPor('notas')}
                  >
                    Notas{' '}
                    <span className="text-cyan-600">
                      {columnaOrden === 'notas'
                        ? direccionOrden === 'asc'
                          ? '▲'
                          : '▼'
                        : ''}
                    </span>
                  </th>

                  <th className="whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Acciones
                  </th>

                  {nombresCamposExtras.map((campo) => (
                    <th
                      key={campo}
                      className="hidden whitespace-nowrap border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 lg:table-cell"
                    >
                      {campo}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td
                      colSpan={19 + nombresCamposExtras.length}
                      className="px-4 py-8 text-center text-sm text-slate-500"
                    >
                      Cargando instrumentos...
                    </td>
                  </tr>
                ) : errorCarga ? (
                  <tr>
                    <td
                      colSpan={19 + nombresCamposExtras.length}
                      className="bg-red-50 px-4 py-8 text-center text-sm text-red-700"
                    >
                      {errorCarga}
                    </td>
                  </tr>
                ) : instrumentosOrdenados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={19 + nombresCamposExtras.length}
                      className="px-4 py-8 text-center text-sm text-slate-500"
                    >
                      No hay instrumentos registrados
                    </td>
                  </tr>
                ) : (
                  instrumentosOrdenados.map((ins) => {
                    let estadoTexto = 'Sin fecha';
                    let estadoClase =
                      'border-slate-200 bg-slate-50 text-slate-600';
                    let bordeEstado = 'border-l-slate-300';

                    if (ins.fechaProxima) {
                      const fechaProx = new Date(ins.fechaProxima);
                      const hoy = new Date();

                      const diferenciaDias = Math.floor(
                        (fechaProx - hoy) / (1000 * 60 * 60 * 24)
                      );

                      if (diferenciaDias < 0) {
                        estadoTexto = 'Vencido';
                        estadoClase =
                          'border-red-200 bg-red-50 text-red-700';
                        bordeEstado = 'border-l-red-400';
                      } else if (diferenciaDias <= 30) {
                        estadoTexto = 'Por vencer';
                        estadoClase =
                          'border-amber-200 bg-amber-50 text-amber-700';
                        bordeEstado = 'border-l-amber-400';
                      } else {
                        estadoTexto = 'Vigente';
                        estadoClase =
                          'border-emerald-200 bg-emerald-50 text-emerald-700';
                        bordeEstado = 'border-l-emerald-400';
                      }
                    }

                    const celdaClase =
                      'whitespace-nowrap border-b border-slate-100 px-3 py-3 text-sm text-slate-700';

                    return (
                      <tr
                        key={ins.id}
                        className="transition-colors hover:bg-slate-50"
                      >
                        <td
                          className={`${celdaClase} border-l-4 ${bordeEstado} font-medium text-slate-900`}
                        >
                          {ins.nombre}
                        </td>

                        <td className={celdaClase}>
                          {ins.identificacion}
                        </td>

                        <td className={celdaClase}>
                          {ins.rango}
                        </td>

                        <td className={`${celdaClase} hidden md:table-cell`}>
                          {ins.marca}
                        </td>

                        <td className={`${celdaClase} hidden md:table-cell`}>
                          {ins.modelo}
                        </td>

                        <td className={`${celdaClase} hidden lg:table-cell`}>
                          {ins.serie}
                        </td>

                        <td className={celdaClase}>
                          {ins.tolerancia}
                        </td>

                        <td className={celdaClase}>
                          {ins.tipo}
                        </td>

                        <td className={`${celdaClase} hidden md:table-cell`}>
                          {ins.condicion}
                        </td>

                        <td className={`${celdaClase} hidden lg:table-cell`}>
                          {ins.certificado}
                        </td>

                        <td className={`${celdaClase} hidden lg:table-cell`}>
                          {ins.fechaUltima}
                        </td>

                        <td className={`${celdaClase} hidden lg:table-cell`}>
                          <div>{ins.fechaProxima || '—'}</div>

                          <span
                            className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${estadoClase}`}
                          >
                            {estadoTexto}
                          </span>
                        </td>

                        <td className={celdaClase}>
                          {ins.carta}
                        </td>

                        <td className={`${celdaClase} hidden md:table-cell`}>
                          {ins.ubicacion}
                        </td>

                        <td className={`${celdaClase} hidden lg:table-cell`}>
                          {ins.asignado}
                        </td>

                        <td className={`${celdaClase} hidden xl:table-cell`}>
                          {ins.proveedor}
                        </td>

                        <td className={`${celdaClase} hidden xl:table-cell`}>
                          {ins.precio}
                        </td>

                        <td className={`${celdaClase} hidden xl:table-cell`}>
                          {ins.notas}
                        </td>

                        <td className={`${celdaClase} text-center`}>
                          {ins.activo && (
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() =>
                                  navigate(`/instrumentos/editar/${ins.id}`)
                                }
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
                              >
                                Editar
                              </button>

                              <button
                                onClick={() => handleEliminar(ins.id)}
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                              >
                                Eliminar
                              </button>
                            </div>
                          )}
                        </td>

                        {nombresCamposExtras.map((campo) => (
                          <td
                            key={campo}
                            className={`${celdaClase} hidden lg:table-cell`}
                          >
                            {ins[campo] || ''}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Instrumentos;